import React, { useContext, useMemo } from "react";
import { PrecisionConfig } from "types";
import { RootState } from "app/store";
import AssetsTable from "features/portfolio/Assets/AssetsTable";
import { ConsolidatedCoinData, LanguageNameSpaces } from "@shared/types";
import EmptyView from "common/Empty/EmptyView";
import { portfolioFilters } from "@shared/slices/filters.slice";
import { useAssetsData } from "features/portfolio/common/hooks";
import { EmptyStatesType } from "common/Icons/types.ts";
import { DrawerContext } from "common/Drawers/Drawer.context";
import { PortfolioContext } from "features/portfolio/Portfolio.context";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

export interface CoinTableContentProps {
  isLoading?: boolean;
  isLoaded?: boolean;
  dataSelector: (state: RootState) => ConsolidatedCoinData[];
  precisionSelector: (state: RootState) => PrecisionConfig | undefined;
  clientToExchangeMapping?: Record<string, string>;
  searchValue?: string;
}

export const CoinTableContent = ({
  isLoading,
  dataSelector,
  precisionSelector,
  clientToExchangeMapping,
  isLoaded,
  searchValue: propsSearchValue,
}: CoinTableContentProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  const { isDrawer } = useContext(DrawerContext) || {};
  const { searchValue } = useContext(PortfolioContext) || {};
  const searchFilter = propsSearchValue ?? searchValue;
  const { rawPortfolioData, filteredCoinsData } = useAssetsData({
    dataSelector,
    searchFilter,
  });

  const emptyState =
    isLoaded && filteredCoinsData ? (
      <EmptyView
        iconKey={EmptyStatesType.Coins}
        title={t("assets.empty_title")}
        iconWidth={isDrawer ? 100 : 140}
        objectName="coins"
        isSearch={!!searchFilter?.length}
        hasData={!!rawPortfolioData?.length}
        filtersSlice={portfolioFilters}
      />
    ) : undefined;

  return useMemo(() => {
    return (
      <AssetsTable
        data={filteredCoinsData}
        loading={!!isLoading}
        clientToExchangeMapping={clientToExchangeMapping}
        precisionSelector={precisionSelector}
        emptyState={emptyState}
      />
    );
  }, [filteredCoinsData, isLoading, searchFilter]);
};
